import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, ShieldAlert, TrendingUp } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, color }) {
  return (
    <div className="bg-elevated border border-edge rounded-sm p-3">
      <div className="text-[9px] uppercase tracking-wider text-muted2">{label}</div>
      <div className="mono text-base font-bold mt-1" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}

export default function Report() {
  const [r, setR] = useState(null);
  const [sw, setSw] = useState(null);
  const [eod, setEod] = useState([]);
  const [strategy, setStrategy] = useState("SCALP");

  const load = () => {
    api.paperReport().then(setR);
    api.eod().then((d) => setEod(d.summaries || []));
    api.getConfig().then((c) => setStrategy(c.strategy || "SCALP")).catch(() => {});
    api.swing().then(setSw).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(() => { api.paperReport().then(setR); api.swing().then(setSw).catch(() => {}); }, 4000);
    return () => clearInterval(t);
  }, []);

  if (!r) return <div className="p-6 text-muted2 text-sm">Loading report…</div>;

  const eligible = r.go_live_eligible;
  const isSwing = strategy === "SWING";

  const approve = async (v) => {
    const nr = await api.approve(v);
    setR(nr);
    toast.success(v ? "Approval granted" : "Approval revoked");
  };

  return (
    <div className="pb-40">
      <div className="glass sticky top-0 z-30 border-b border-edge px-4 py-3">
        <h1 className="text-xl font-bold tracking-tight">{isSwing ? "Swing Performance" : "Paper Test Report"}</h1>
        <p className="text-[11px] text-muted2">{isSwing ? "Live CNC delivery results" : "Aggregated live-paper performance"}</p>
      </div>

      <div className="p-4 space-y-4">
        {isSwing ? <SwingReport sw={sw} /> : <PaperReport r={r} />}

        {/* manual approval — shared */}
        <div className="bg-surface border border-edge rounded-sm p-4" data-testid="approval-panel">
          {isSwing && (
            <div className="mb-3 bg-pos/10 border border-pos/30 rounded-sm px-3 py-2 text-[11px] text-pos" data-testid="swing-approval-note">
              SWING strategy active — the paper win-rate gate is skipped. Tick below + log in to Kite to unlock Live.
            </div>
          )}
          <div className="flex items-start gap-3">
            <Checkbox
              checked={r.manual_approved}
              onCheckedChange={(v) => approve(!!v)}
              disabled={!isSwing && !eligible && !r.manual_approved}
              className="mt-0.5 border-edge data-[state=checked]:bg-pos data-[state=checked]:border-pos"
              data-testid="approve-checkbox"
            />
            <div>
              <div className="text-sm font-semibold text-white">
                {isSwing ? "I understand this places REAL delivery (CNC) orders" : "I have reviewed the paper test results"}
              </div>
              <div className="text-[11px] text-muted2 mt-0.5">
                {isSwing
                  ? "Manual approval + a live Kite login unlock Live for the autonomous swing bot."
                  : `Manual approval is gate #2. Both the ${r.gate}% win-rate gate and this approval must be true — plus a live Kite login — before Live unlocks.`}
              </div>
            </div>
          </div>
        </div>

        {/* EOD summaries (scalp sessions) */}
        {!isSwing && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted2 mb-2">EOD Summaries</div>
            <div className="border border-edge rounded-sm overflow-hidden bg-surface divide-y divide-edge/50" data-testid="eod-list">
              {eod.map((s) => (
                <div key={s.session_date} className="flex items-center justify-between px-3 py-2.5">
                  <div>
                    <div className="mono text-xs text-white">{s.session_date}</div>
                    <div className="text-[10px] text-muted2">{s.trades} trades · WR {s.win_rate}%</div>
                  </div>
                  <div className="mono text-sm font-bold" style={{ color: s.net_pnl >= 0 ? "#00E676" : "#FF3B30" }}>{fmtINR(s.net_pnl)}</div>
                </div>
              ))}
              {eod.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted2">No sessions finalized yet. End a day in Config.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PaperReport({ r }) {
  const eligible = r.go_live_eligible;
  return (
    <>
      <div className={`rounded-sm p-5 border text-center ${eligible ? "bg-pos/10 border-pos/50" : "bg-neg/10 border-neg/40"}`} data-testid="golive-badge">
        <div className="flex items-center justify-center gap-2">
          {eligible ? <ShieldCheck className="text-pos" size={22} /> : <ShieldAlert className="text-neg" size={22} />}
          <span className={`text-base font-black tracking-[0.15em] ${eligible ? "text-pos" : "text-neg"}`}>
            {eligible ? "GO-LIVE ELIGIBLE" : "NOT ELIGIBLE"}
          </span>
        </div>
        <div className="mono text-5xl font-extrabold mt-3" style={{ color: eligible ? "#00E676" : "#FF3B30" }} data-testid="report-winrate">
          {r.win_rate}%
        </div>
        <div className="text-[11px] text-muted2 mt-1">
          win-rate · gate ≥ {r.gate}% · {r.total_trades} trades (min {r.min_trades})
        </div>
      </div>

      <div className={`rounded-sm px-3 py-2.5 border flex items-center justify-between ${r.clears_breakeven ? "bg-pos/[0.06] border-pos/30" : "bg-warn/10 border-warn/30"}`}>
        <span className="text-xs text-white">Breakeven win-rate needed</span>
        <span className={`mono text-sm font-bold ${r.clears_breakeven ? "text-pos" : "text-warn"}`}>{r.breakeven_winrate}%</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="Trades" value={r.total_trades} />
        <Stat label="Wins" value={r.wins} color="#00E676" />
        <Stat label="Losses" value={r.losses} color="#FF3B30" />
        <Stat label="Net P&L" value={fmtINR(r.net_pnl)} color={r.net_pnl >= 0 ? "#00E676" : "#FF3B30"} />
        <Stat label="Avg R" value={r.avg_r_multiple} />
        <Stat label="Max DD" value={fmtINR(r.max_drawdown)} color="#FF3B30" />
        <Stat label="Gross" value={fmtINR(r.gross_pnl)} />
        <Stat label="Costs" value={fmtINR(r.total_costs)} color="#FFB300" />
        <Stat label="Cost Impact" value={`${r.cost_impact_pct}%`} color="#FFB300" />
      </div>
    </>
  );
}

function SwingReport({ sw }) {
  if (!sw) return <div className="text-muted2 text-sm py-6 text-center">Loading swing results…</div>;
  const rep = sw.report || {};
  const net = rep.net_pnl || 0;
  const rc = rep.reason_counts || {};
  const unreal = (sw.positions || []).reduce((s, p) => s + (p.pnl || 0), 0);
  return (
    <>
      <div className={`rounded-sm p-5 border text-center ${net >= 0 ? "bg-pos/10 border-pos/50" : "bg-neg/10 border-neg/40"}`} data-testid="swing-report-badge">
        <div className="flex items-center justify-center gap-2">
          <TrendingUp className={net >= 0 ? "text-pos" : "text-neg"} size={20} />
          <span className={`text-base font-black tracking-[0.15em] ${net >= 0 ? "text-pos" : "text-neg"}`}>REALIZED NET P&L</span>
        </div>
        <div className="mono text-4xl font-extrabold mt-3" style={{ color: net >= 0 ? "#00E676" : "#FF3B30" }} data-testid="swing-report-net">
          {fmtINR(net)}
        </div>
        <div className="text-[11px] text-muted2 mt-1">
          {rep.booked_trades || 0} booked trades · win-rate {rep.win_rate || 0}%
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="Booked" value={rep.booked_trades || 0} />
        <Stat label="Wins" value={rep.wins || 0} color="#00E676" />
        <Stat label="Losses" value={rep.losses || 0} color="#FF3B30" />
        <Stat label="Gross" value={fmtINR(rep.gross_pnl || 0)} color={(rep.gross_pnl || 0) >= 0 ? "#00E676" : "#FF3B30"} />
        <Stat label="Costs" value={fmtINR(rep.total_costs || 0)} color="#FFB300" />
        <Stat label="Avg / Trade" value={fmtINR(rep.avg_net || 0)} color={(rep.avg_net || 0) >= 0 ? "#00E676" : "#FF3B30"} />
        <Stat label="Best" value={fmtINR(rep.best || 0)} color="#00E676" />
        <Stat label="Worst" value={fmtINR(rep.worst || 0)} color="#FF3B30" />
        <Stat label="Unrealized" value={fmtINR(unreal)} color={unreal >= 0 ? "#00E676" : "#FF3B30"} />
      </div>

      <div className="bg-surface border border-edge rounded-sm p-3" data-testid="swing-exit-breakdown">
        <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted2 mb-2">Exit Reasons</div>
        <div className="flex flex-wrap gap-2">
          {["TARGET", "STOP", "BREAKEVEN", "MANUAL", "EXTERNAL_EXIT"].map((k) => (
            <div key={k} className="flex items-center gap-1.5 bg-elevated border border-edge rounded-sm px-2.5 py-1.5">
              <span className="text-[10px] font-bold tracking-wider" style={{
                color: { TARGET: "#00E676", STOP: "#FF3B30", BREAKEVEN: "#14B8A6", MANUAL: "#007AFF", EXTERNAL_EXIT: "#8A919E" }[k],
              }}>{k === "EXTERNAL_EXIT" ? "EXTERNAL" : k === "BREAKEVEN" ? "B/E" : k}</span>
              <span className="mono text-xs font-bold text-white">{rc[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
